import { useEffect, useMemo, useState } from "react";
import {
  getHistoryService,
  getHistoryServices,
} from "../../../../common/network/Api";
import { getApiErrorMessage } from "../../../../common/utils/apiError";
import { ServiceContent } from "../../../new-reports/types/newReportTypes";
import {
  HistoryServiceDetail,
  ServiceHistoryRow,
  historyClientName,
} from "../../entities/history";
import { HistoryScope } from "./useHistoryScope";
import useHistoryList, { HistoryListResult } from "./useHistoryList";
import {
  normalizeServiceContent,
  toServiceHistoryRow,
} from "./serviceHistoryMapper";

/**
 * History → Programming's data source: `GET /history/services` for the
 * listing, `GET /history/services/{id}` for one record.
 *
 * Scoping is the endpoint's: `userid` narrows to the workspace subject in the
 * client and customer scopes and is omitted in the cross-client view. The
 * token is always the logged-in user's — the endpoint serves a company's
 * clients and a customer's own history alike.
 */

export interface ServiceHistoryResult
  extends Omit<HistoryListResult<any>, "rows"> {
  rows: ServiceHistoryRow[];
}

export const useServiceHistory = (scope: HistoryScope): ServiceHistoryResult => {
  const { kind, subjectId } = scope;
  const list = useHistoryList({
    fetchPage: getHistoryServices,
    cacheKey: "services",
    userid: kind === "company" ? undefined : subjectId,
    // The client workspace resolves its subject asynchronously; don't fetch
    // the whole company while waiting.
    enabled: kind === "company" || Boolean(subjectId),
  });

  const rows = useMemo(() => list.rows.map(toServiceHistoryRow), [list.rows]);

  return { ...list, rows };
};

export interface ServiceHistoryDetailResult {
  service: ServiceContent | null;
  /** The client this service was prescribed to; "" when unknown. */
  clientName: string;
  /** The prescription this service belongs to; null when not linked. */
  prescriptionId: string | null;
  loading: boolean;
  notFound: boolean;
  error: string | null;
}

/** One prescribed service, by the listing row's id. Works cold from a URL. */
export const useServiceHistoryDetail = (
  serviceId: string | undefined
): ServiceHistoryDetailResult => {
  const [detail, setDetail] = useState<HistoryServiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    setDetail(null);

    if (!serviceId) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    getHistoryService(serviceId)
      .then((response) => {
        if (cancelled) return;
        // The payload is the report's ServiceContent: no id, so "found" means
        // it names a service.
        if (!response || !response.serviceName) {
          setNotFound(true);
        } else {
          setDetail(response);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("Error loading service history detail:", e);
        if (e?.response?.status === 404) {
          setNotFound(true);
        } else {
          setError(getApiErrorMessage(e, "Couldn't load this service."));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [serviceId]);

  const service = useMemo(
    () => (detail ? normalizeServiceContent(detail) : null),
    [detail]
  );

  return {
    service,
    clientName: detail ? historyClientName(detail) : "",
    prescriptionId: detail?.customerPrescriptionID || null,
    loading,
    notFound,
    error,
  };
};
